import { useEffect, useRef, type ReactNode } from 'react';
import clsx from 'clsx';
import { X } from '@/components/icons';

/**
 * Accessible modal built on the native <dialog> element (focus trap, Esc to close, inert background).
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // Click on the backdrop (the dialog element itself) closes it.
        if (e.target === ref.current) onClose();
      }}
      className={clsx(
        'm-auto w-[calc(100%-2rem)] rounded-panel bg-surface p-0 text-fg shadow-2xl ring-1 ring-border open:animate-slide-up',
        size === 'sm' && 'max-w-md',
        size === 'md' && 'max-w-lg',
        size === 'lg' && 'max-w-2xl',
      )}
    >
      {open && (
        <div className="flex max-h-[85vh] flex-col">
          <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3">
            <div>
              <h2 className="text-lg font-semibold">{title}</h2>
              {description && <p className="mt-1 text-sm text-fg-secondary">{description}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-card p-1.5 text-fg-muted transition hover:bg-surface-raised hover:text-fg"
              aria-label="Close"
            >
              <X className="size-5" />
            </button>
          </div>
          <div className="overflow-y-auto px-6 pb-5">{children}</div>
          {footer && (
            <div className="flex justify-end gap-2 rounded-b-panel border-t border-border-subtle bg-surface-sunken/70 px-6 py-3.5">
              {footer}
            </div>
          )}
        </div>
      )}
    </dialog>
  );
}
